import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { Toggle } from '../../../shared/toggle';
import styles from './AIPlayground.module.scss';
import { togglePlaygroundQuestion, updateAIPlaygroundSearch, getAIPlaygroundSearchTerm, getFilteredQueries, getDocumentNameIdQueries, getCurrentSelectedQueries, getIsFilteringSelectedQueries, PlaygroundQuestion } from './store';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { OverflowTooltip } from '../../../shared/tooltip';
import { Text } from '../../../shared/text/Text';
import { PageLoadingDots } from '../../../shared/loading-dots/PageLoadingDots';

const { lightGrey } = styles;

export const AIPlaygroundQuerySelect: React.FC = () => {
    const dispatch = useAppDispatch();

    const selectedQueries = useAppSelector(getCurrentSelectedQueries);

    const aiPlaygroundSearchTerm = useAppSelector(getAIPlaygroundSearchTerm);
    const totalQueries = useAppSelector(getDocumentNameIdQueries);
    const filteredQueries = useAppSelector(getFilteredQueries);
    const isFilteringQueries = useAppSelector(getIsFilteringSelectedQueries);

    const toggleQuestion = useCallback((question: PlaygroundQuestion) => dispatch(togglePlaygroundQuestion(question)), [dispatch]);

    const isChecked = useCallback((queryKey: string) => selectedQueries.map(({ queryKey }) => queryKey).includes(queryKey), [selectedQueries]);

    const allSelected = useMemo(() => filteredQueries.length > 0 ? filteredQueries.map(({ queryKey }) => queryKey).every(queryKey => selectedQueries.map(({ queryKey }) => queryKey).includes(queryKey)) : false, [filteredQueries, selectedQueries]);

    const toggleAll = useCallback(() => {
        if (allSelected && !filteredQueries.length) {
            // then toggle all the selected queries
            selectedQueries.forEach(question => toggleQuestion(question));
            // then toggle only the filtered questions
        } else if (allSelected && filteredQueries.length > 0) {
            filteredQueries.forEach(({ documentQueryId, question, guidanceNotes, queryKey }) => toggleQuestion({ documentQueryId: documentQueryId!, question, guidanceNotes, queryKey, correct: false }));
        } else {
            // go through the ones not in the selected and toggle those on
            const queriesToInclude = filteredQueries.filter(({ queryKey }) => !selectedQueries.map(({ queryKey }) => queryKey).includes(queryKey));
            queriesToInclude.forEach(({ documentQueryId, question, guidanceNotes, queryKey }) => toggleQuestion({ documentQueryId: documentQueryId!, question, guidanceNotes, queryKey, correct: false }));
        }
    }, [allSelected, filteredQueries, toggleQuestion, selectedQueries]);

    const searchAIPlaygroundQueries = useCallback((searchTerm: string) => dispatch(updateAIPlaygroundSearch(searchTerm)), [dispatch]);

    if (aiPlaygroundSearchTerm === '' && filteredQueries.length === 0) {
        return (
            <div className={styles.aiPlaygroundQueries}>
                <div className={styles.aiPlaygroundEmptyQueries}>You have no questions configured for the agreement type</div>
            </div>
        );
    }

    return (
        <div className={styles.aiPlaygroundQueries}>
            <div className={styles.aiPlaygroundQuerySelectAllWrapper}>
                <div className={styles.aiPlaygroundSearchWrapper}>
                    <Text
                        onChange={e => searchAIPlaygroundQueries(e.target.value)}
                        value={aiPlaygroundSearchTerm}
                        maxLength={256}
                        testId='ai-playground-query-search'
                        placeholder='Search...'
                        marginBottom='0px'
                        borderColour={lightGrey}
                    />
                </div>
                <div className={styles.aiPlaygroundSelectAllWrapper}>
                    <div className={styles.aiPlaygroundQuerySelectAllLabel}>Select All</div>
                    <Toggle onChange={toggleAll} checked={allSelected} />
                </div>
            </div>
            {isFilteringQueries ? <div className={styles.spinnerWrapper}><PageLoadingDots /></div> :
                <div className={styles.aiPlaygroundQuerySelectWrapper}>
                    <Scrollable>
                        {filteredQueries.length === 0 ?
                            <div>No questions match this search input</div> :
                            filteredQueries.map(({ documentQueryId, queryKey, correctQueries, question, totalQueries, queryOrder, percentage, guidanceNotes }) => {
                                const checked = isChecked(queryKey);
                                return (
                                    <div key={queryKey} className={styles.aiPlaygroundQueryAndToggleWrapper}>
                                        <div className={styles.aiPlaygroundQueryWrapper} key={documentQueryId} id={`ai-query-${queryOrder}`}>
                                            <div className={styles.aiPlaygroundDataPointWrapper}>
                                                <div>{question}</div>
                                            </div>
                                            {documentQueryId &&
                                                <div className={styles.percentageWrapper}>
                                                    <div className={styles.percentageBar}>
                                                        <div className={styles.successPercentage} style={{ width: `${percentage}%` }} />
                                                    </div>
                                                    <div className={styles.successInfo}>
                                                        <div className={styles.successString}>
                                                            <OverflowTooltip overlayText={`Success rate: ${correctQueries} / ${totalQueries}`} />
                                                        </div>
                                                        <div className={styles.displayPercentage}>{percentage}%</div>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                        <div className={styles.aiPlaygroundQueryToggleWrapper}>
                                            <Toggle
                                                onChange={() => toggleQuestion({ documentQueryId: documentQueryId!, question, guidanceNotes, queryKey, correct: false })}
                                                checked={checked}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                    </Scrollable>
                </div>
            }
            <div className={styles.totalSelectedQueries}>{`Selected: ${selectedQueries.length} / ${totalQueries.length}`}</div>
        </div>
    );
};
